import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { FiMail, FiUser, FiPhone, FiLock, FiCheckCircle, FiChevronRight, FiEye, FiEyeOff } from 'react-icons/fi';

const RegisterStepOne = ({ onNext }) => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        password: '',
        password_confirm: ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await axios.post(`${config.API_BASE_URL}/users/register-step-one/`, formData, {
                withCredentials: true,
            });
            if (response.status === 200) {
                // console.log('Step 1 response:', response.data);
                if (response.data.user_id) {
                    // Store user ID in localStorage
                    localStorage.setItem('tempUserId', response.data.user_id);
                    // Pass the user ID directly to the next step
                    onNext(response.data.user_id);
                } else {
                    // console.error('No user_id in response:', response.data);
                    setErrors({ general: 'Failed to get user ID from server' });
                }
            }
        } catch (error) {
            if (error.response) {
                setErrors(error.response.data);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {/* Registration Form Section */}
            <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 py-10">
                <div className="max-w-6xl mx-auto p-12">
                    <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden">
                        <div className="p-12 md:p-16">
                            <div className="text-center mb-12">
                                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                                    Create your account
                                </h2>
                                <p className="text-gray-600 dark:text-gray-300">
                                    Join our community and start connecting with amazing events and services
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid sm:grid-cols-2 gap-8">
                                    {/* First Name and Last Name Side by Side */}
                                    <div>
                                        <div className="relative">
                                            <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            <input
                                                name="first_name"
                                                type="text"
                                                className="pl-10 w-full px-4 py-3.5 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400"
                                                value={formData.first_name}
                                                onChange={handleChange}
                                                placeholder="First Name"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <div className="relative">
                                            <FiUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            <input
                                                name="last_name"
                                                type="text"
                                                className="pl-10 w-full px-4 py-3.5 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400"
                                                value={formData.last_name}
                                                onChange={handleChange}
                                                placeholder="Last Name"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Email and Phone Side by Side */}
                                    <div>
                                        <div className="relative">
                                            <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            <input
                                                name="email"
                                                type="email"
                                                className="pl-10 w-full px-4 py-3.5 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="Email Address"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <div className="relative">
                                            <FiPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            <input
                                                name="phone"
                                                type="tel"
                                                className="pl-10 w-full px-4 py-3.5 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="Phone Number"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Password Fields Full Width */}
                                    <div className="sm:col-span-2 relative">
                                        <div className="relative">
                                            <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            <input
                                                name="password"
                                                type={showPassword ? "text" : "password"}
                                                className="pl-10 pr-12 w-full px-4 py-3.5 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400"
                                                value={formData.password}
                                                onChange={handleChange}
                                                placeholder="Password"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg transition-colors"
                                            >
                                                {showPassword ? (
                                                    <FiEyeOff className="w-5 h-5" />
                                                ) : (
                                                    <FiEye className="w-5 h-5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="sm:col-span-2 relative">
                                        <div className="relative">
                                            <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                            <input
                                                name="password_confirm"
                                                type={showConfirmPassword ? "text" : "password"}
                                                className="pl-10 pr-12 w-full px-4 py-3.5 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all placeholder:text-gray-500 dark:placeholder:text-gray-400"
                                                value={formData.password_confirm}
                                                onChange={handleChange}
                                                placeholder="Confirm Password"
                                                required
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg transition-colors"
                                            >
                                                {showConfirmPassword ? (
                                                    <FiEyeOff className="w-5 h-5" />
                                                ) : (
                                                    <FiEye className="w-5 h-5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {Object.keys(errors).length > 0 && (
                                    <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-4 rounded-lg">
                                        <FiCheckCircle className="inline-block w-5 h-5 mr-2" />
                                        {Object.values(errors).join('. ') || 'An error occurred during registration'}
                                    </div>
                                )}

                                <div className="mt-8">
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full flex items-center justify-center py-4 px-8 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Processing...
                                            </>
                                        ) : (
                                            'Sign up'
                                        )}
                                    </button>
                                </div>

                                <div className="sm:flex justify-between items-center text-gray-600 dark:text-gray-300 flex-col gap-4">
                                    <span className="text-sm">
                                        Already have an account?
                                    </span>
                                    <Link
                                        to="/login"
                                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium flex items-center gap-1"
                                    >
                                        Login here
                                        <FiChevronRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default RegisterStepOne;
